<template>
  <el-container class="exam-container">
    <el-aside width="280px" class="exam-aside">
      <div class="aside-card timer-box">
        <div class="timer-title"><i class="el-icon-time" /> 剩余时间</div>
        <div class="timer-display" :class="{ 'time-danger': !timeLoading && remainTime < 300 }">
          {{ formatRemainTime }}
        </div>
      </div>

      <div class="aside-card answer-card">
        <h4 class="card-title">答题卡</h4>
        <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="card-group">
          <div class="group-label">{{ section.sectionName }}</div>
          <div class="number-grid">
            <span
              v-for="q in section.flatQuestions"
              :key="q.questionId"
              class="number-item"
              :class="{
                'is-answered': checkIsAnswered(q),
                'is-active': currentActiveId === q.questionId
              }"
              @click="scrollToQuestion(q.questionId)"
            >
              {{ q.number }}
            </span>
          </div>
        </div>

        <el-button type="success" class="btn-submit" icon="el-icon-upload2" @click="handleConfirmSubmit">
          立即交卷
        </el-button>
      </div>
    </el-aside>

    <el-main class="exam-main">
      <div class="paper-header">
        <h2 class="paper-title">{{ paperInfo.title }}</h2>
        <div class="paper-meta">
          <span>测评科目：{{ paperInfo.subjectName || '未指定' }}</span>
        </div>
      </div>

      <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="question-group-section">
        <h3 class="group-header-title">
          {{ section.sectionName }} <span class="group-desc">（{{ section.sectionHint }}）</span>
        </h3>

        <div
          v-for="q in section.questions"
          :id="'q-' + q.questionId"
          :key="q.questionId"
          class="question-card"
          @mouseenter="currentActiveId = q.questionId"
        >
          <template v-if="q.typeCode === 1">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
            </div>
            <div class="question-body">
              <el-radio-group v-model="answers[q.questionId]" @change="handleAnswerChange(q.questionId)">
                <el-radio v-for="opt in q.options" :key="opt.pos" :label="opt.pos" class="option-item">
                  {{ opt.content }}
                </el-radio>
              </el-radio-group>
            </div>
          </template>

          <template v-else-if="q.typeCode === 2">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
              <el-tag type="warning" size="mini" class="type-tag">多选</el-tag>
            </div>
            <div class="question-body">
              <el-checkbox-group v-model="answers[q.questionId]" @change="handleAnswerChange(q.questionId)">
                <el-checkbox v-for="opt in q.options" :key="opt.pos" :label="opt.pos" class="option-item checkbox-item">
                  {{ opt.content }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </template>

          <template v-else-if="q.typeCode === 3">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
            </div>
            <div class="question-body completion-body">
              <div v-for="(item, index) in answers[q.questionId]" :key="index" class="completion-item">
                <span class="completion-prefix">第 {{ index + 1 }} 空：</span>
                <el-input
                  v-model="answers[q.questionId][index]"
                  size="small"
                  placeholder="请输入答案"
                  @change="handleAnswerChange(q.questionId)"
                />
              </div>
            </div>
          </template>

          <template v-else-if="q.typeCode === 4">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
            </div>
            <div class="question-body">
              <el-input
                v-model="answers[q.questionId]"
                type="textarea"
                :rows="6"
                placeholder="请输入作答内容..."
                @change="handleAnswerChange(q.questionId)"
              />
            </div>
          </template>

          <template v-else-if="q.typeCode === 5">
            <div class="question-stem material-stem">
              <span class="material-tag">【组合材料】</span>
              <div class="q-text" style="white-space: pre-wrap;">{{ q.content }}</div>
            </div>

            <div class="sub-questions-container">
              <div v-for="subQ in q.children" :key="subQ.questionId" class="sub-question-item">
                <div class="question-stem sub-stem">
                  <span class="q-number">{{ subQ.number }}.</span>
                  <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                  <span class="q-text">{{ subQ.content }}</span>
                </div>

                <div class="question-body">
                  <template v-if="subQ.typeCode === 1">
                    <el-radio-group v-model="answers[subQ.questionId]" @change="handleAnswerChange(subQ.questionId)">
                      <el-radio v-for="opt in subQ.options" :key="opt.pos" :label="opt.pos" class="option-item">
                        {{ opt.content }}
                      </el-radio>
                    </el-radio-group>
                  </template>

                  <template v-if="subQ.typeCode === 2">
                    <el-checkbox-group v-model="answers[subQ.questionId]" @change="handleAnswerChange(subQ.questionId)">
                      <el-checkbox v-for="opt in subQ.options" :key="opt.pos" :label="opt.pos" class="option-item checkbox-item">
                        {{ opt.content }}
                      </el-checkbox>
                    </el-checkbox-group>
                  </template>

                  <template v-else-if="subQ.typeCode === 3">
                    <div class="completion-body">
                      <div v-for="(item, index) in answers[subQ.questionId]" :key="index" class="completion-item">
                        <span class="completion-prefix">第 {{ index + 1 }} 空：</span>
                        <el-input
                          v-model="answers[subQ.questionId][index]"
                          size="small"
                          placeholder="请输入答案"
                          @change="handleAnswerChange(subQ.questionId)"
                        />
                      </div>
                    </div>
                  </template>

                  <template v-else-if="subQ.typeCode === 4">
                    <div class="completion-body">
                      <el-input
                        v-model="answers[subQ.questionId]"
                        type="textarea"
                        :rows="6"
                        placeholder="请输入作答内容..."
                        @change="handleAnswerChange(subQ.questionId)"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { cacheUserAnswer, getCachedUserAnswer, getExamInfo, submitExam } from '@/api/exam'

export default {
  name: 'ExamPaper',
  data() {
    return {
      queryParams: {
        examId: 0
      },
      remainTime: 0,
      timeLoading: true,
      timer: null,
      currentActiveId: null,
      answers: {},
      paperInfo: {},
      sectionsList: []
    }
  },
  computed: {
    formatRemainTime() {
      if (this.timeLoading) {
        return '正在获取时间...'
      }
      const hours = Math.floor(this.remainTime / 3600).toString().padStart(2, '0')
      const mins = Math.floor((this.remainTime % 3600) / 60).toString().padStart(2, '0')
      const secs = Math.floor(this.remainTime % 60).toString().padStart(2, '0')
      return `${hours}:${mins}:${secs}`
    },
    formattedAnswersSubmit() {
      return Object.keys(this.answers).map(qId => {
        const val = this.answers[qId]
        let answerList = []

        if (Array.isArray(val)) {
          answerList = [...val]
        } else {
          answerList = val && String(val).trim() !== '' ? [val] : []
        }

        return {
          questionId: Number(qId),
          answerList: answerList
        }
      })
    }
  },
  created() {
    const examId = this.$route.query.examId
    if (!examId) {
      this.$message.error('参数缺失，无法加载测评页面！')
      return
    }
    this.queryParams.examId = examId
    this.loadPaperData()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async loadPaperData() {
      try {
        const resp = await getExamInfo(this.queryParams)
        if (resp.code !== 0) {
          this.$message.warning(resp.msg)
          return
        }

        const examData = resp.data
        this.remainTime = examData.duration
        this.timeLoading = false
        this.startTimer()

        const remoteData = examData.paperDetail
        this.paperInfo = {
          paperId: remoteData.paperId,
          title: remoteData.title,
          subjectId: remoteData.subjectId,
          subjectName: remoteData.subjectName
        }

        let globalQuestionIndex = 1
        const localAnswers = {}

        this.sectionsList = remoteData.sections.map(sec => {
          const flatQuestions = []

          const ProcessedQuestions = sec.questions.map(q => {
            if (q.answers && !q.options) {
              q.options = q.answers
            }

            if (q.typeCode !== 5) {
              q.number = globalQuestionIndex++
              flatQuestions.push({ questionId: q.questionId, number: q.number, typeCode: q.typeCode })

              if (q.typeCode === 3) {
                localAnswers[q.questionId] = new Array((q.options || []).length).fill('')
              } else if (q.typeCode === 2) {
                localAnswers[q.questionId] = []
              } else {
                localAnswers[q.questionId] = ''
              }
            } else if (q.typeCode === 5 && q.children) {
              q.children.forEach(subQ => {
                if (subQ.answers && !subQ.options) {
                  subQ.options = subQ.answers
                }
                subQ.number = globalQuestionIndex++
                flatQuestions.push({ questionId: subQ.questionId, number: subQ.number, typeCode: subQ.typeCode })

                if (subQ.typeCode === 3) {
                  localAnswers[subQ.questionId] = new Array((subQ.options || []).length).fill('')
                } else if (subQ.typeCode === 2) {
                  localAnswers[subQ.questionId] = []
                } else {
                  localAnswers[subQ.questionId] = ''
                }
              })
            }
            return q
          })

          return {
            sectionId: sec.sectionId,
            sectionName: sec.sectionName,
            sectionHint: sec.sectionHint,
            questions: ProcessedQuestions,
            flatQuestions: flatQuestions
          }
        })

        this.answers = localAnswers

        const cachedAnswersArray = await this.getCacheAnswer()

        if (cachedAnswersArray && Array.isArray(cachedAnswersArray)) {
          const updatedAnswers = { ...this.answers }

          cachedAnswersArray.forEach(item => {
            const qId = item.questionId
            const savedList = item.answerList

            if (updatedAnswers[qId] !== undefined && savedList && savedList.length > 0) {
              const isArrayType = Array.isArray(updatedAnswers[qId])

              if (isArrayType) {
                if (updatedAnswers[qId].length > 0 && typeof updatedAnswers[qId][0] === 'string') {
                  updatedAnswers[qId] = updatedAnswers[qId].map((v, index) => {
                    return savedList[index] !== undefined ? savedList[index] : ''
                  })
                } else {
                  updatedAnswers[qId] = [...savedList]
                }
              } else {
                updatedAnswers[qId] = savedList[0] || ''
              }
            }
          })

          this.answers = updatedAnswers
        }
      } catch (error) {
        this.$message.error(error.message || '加载试卷数据失败')
        console.error(error)
      }
    },

    handleAnswerChange(qId) {
      this.cacheAnswer(this.formattedAnswersSubmit)
    },

    cacheAnswer(answersData) {
      const cachedPaperAnswer = {
        examId: this.queryParams.examId,
        paperId: this.paperInfo.paperId,
        answers: answersData
      }
      cacheUserAnswer(cachedPaperAnswer).then(resp => {
        if (resp.code !== 0) console.log(resp.msg)
      })
    },

    async getCacheAnswer() {
      const queryParams = { examId: this.queryParams.examId }
      try {
        const resp = await getCachedUserAnswer(queryParams)
        if (resp.code === 0 && resp.data) {
          return resp.data.answers
        }
      } catch (error) {
        console.error('获取缓存答案失败:', error)
      }
      return null
    },

    startTimer() {
      this.timer = setInterval(() => {
        if (this.remainTime > 0) {
          this.remainTime--
        } else {
          clearInterval(this.timer)
          this.$alert('测评时间已到，系统已自动提交！', '提示', {
            confirmButtonText: '确定',
            callback: () => { this.submitExam() }
          })
        }
      }, 1000)
    },

    checkIsAnswered(question) {
      const ans = this.answers[question.questionId]
      if (ans === undefined || ans === null) return false

      if (Array.isArray(ans)) {
        return ans.some(item => item && String(item).trim() !== '')
      }
      return String(ans).trim() !== ''
    },

    scrollToQuestion(qId) {
      this.currentActiveId = qId
      const targetEl = document.getElementById(`q-${qId}`)
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        const allCards = document.querySelectorAll('.question-card')
        for (const card of allCards) {
          if (card.querySelector(`.sub-questions-container`)) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    },

    // 🛠️【核心修改：formatStem 只接收 content 并纯化内容】
    formatStem(content) {
      if (!content) return ''
      // 仅移除可能存在的冗余尾部 “（X分）” 文本，不进行任何样式标签的拼接
      return content.replace(/（\s*\d+分\s*）/g, '')
    },

    handleConfirmSubmit() {
      let unAnsweredCount = 0
      this.sectionsList.forEach(sec => {
        sec.flatQuestions.forEach(q => {
          if (!this.checkIsAnswered(q)) unAnsweredCount++
        })
      })

      const tipMessage = unAnsweredCount > 0
        ? `您当前还有 ${unAnsweredCount} 道题未完全作答，确定要现在交卷吗？`
        : '您已完成所有题目，确定提交试卷吗？'

      this.$confirm(tipMessage, '交卷确认', {
        confirmButtonText: '确定提交',
        cancelButtonText: '检查一下',
        type: unAnsweredCount > 0 ? 'warning' : 'success'
      }).then(() => {
        this.submitExam()
      }).catch(() => {})
    },

    submitExam() {
      const jsonData = {
        examId: this.queryParams.examId,
        paperId: this.paperInfo.paperId,
        answers: this.formattedAnswersSubmit
      }
      submitExam(jsonData).then(resp => {
        if (resp.code === 0) {
          this.$message.success('试卷提交成功！')
        } else {
          this.$message.warning(resp.msg)
        }
      }).catch(error => {
        this.$message.error(error.message)
      })
    }
  }
}
</script>

<style scoped>
/* 原有基础样式保持不变 */
.exam-container { height: 100vh; background-color: #f0f2f5; }
.exam-aside { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.aside-card { background: #ffffff; border-radius: 8px; padding: 18px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
.timer-box { text-align: center; background: #1e293b; color: #ffffff; }
.timer-title { font-size: 13px; color: #94a3b8; margin-bottom: 6px; }
.timer-display { font-size: 26px; font-family: monospace; font-weight: bold; color: #38bdf8; }
.time-danger { color: #f43f5e !important; animation: blink 1s infinite; }
.answer-card { flex: 1; display: flex; flex-direction: column; overflow-y: auto; }
.card-title { margin: 0 0 14px 0; color: #1e293b; border-left: 3px solid #3b82f6; padding-left: 8px; }
.card-group { margin-bottom: 16px; }
.group-label { font-size: 12px; color: #64748b; margin-bottom: 8px; font-weight: bold;}
.number-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.number-item { height: 36px; display: flex; align-items: center; justify-content: center; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 13px; color: #475569; cursor: pointer; transition: all 0.2s; user-select: none; }
.number-item:hover { border-color: #3b82f6; color: #3b82f6; }
.number-item.is-answered { background-color: #e2f5ec; border-color: #10b981; color: #10b981; }
.number-item.is-active { box-shadow: 0 0 0 2px #3b82f6; font-weight: bold; }
.btn-submit { width: 100%; margin-top: auto; font-weight: bold; height: 40px; }
.exam-main { padding: 20px 40px 40px 20px; height: 100%; overflow-y: auto; }
.paper-header { background: #ffffff; padding: 24px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); text-align: center; }
.paper-title { margin: 0 0 12px 0; color: #1e293b; }
.paper-meta { display: flex; justify-content: center; gap: 24px; font-size: 13px; color: #64748b; }
.question-group-section { margin-bottom: 24px; }
.group-header-title { font-size: 16px; color: #334155; margin-bottom: 12px; }
.group-desc { font-size: 13px; color: #94a3b8; font-weight: normal; }
.question-card { background: #ffffff; border-radius: 8px; padding: 24px; margin-bottom: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.03); border-left: 4px solid transparent; transition: all 0.2s; }
.question-card:hover { border-left-color: #3b82f6; box-shadow: 0 4px 12px rgba(59, 130, 246, 0.08); }
.question-stem { font-size: 15px; color: #1e293b; font-weight: 500; line-height: 1.6; margin-bottom: 16px; }
.q-number { margin-right: 6px; font-weight: bold; }
.question-body { padding-left: 18px; }
.option-item { display: block; margin-bottom: 14px !important; font-size: 14px; color: #334155; white-space: normal; line-height: 1.4; }
.checkbox-item { margin-left: 0 !important; } /* 修复多选框默认左缩进不对齐问题 */
@keyframes blink { 50% { opacity: 0.7; } }

.material-stem { background-color: #f8fafc; padding: 16px; border-radius: 6px; border: 1px dashed #cbd5e1; color: #334155; }
.material-tag { color: #3b82f6; font-weight: bold; display: block; margin-bottom: 6px; }
.sub-questions-container { margin-top: 20px; padding-left: 16px; border-left: 2px solid #e2e8f0; }
.sub-question-item { margin-bottom: 20px; padding-bottom: 10px; }
.sub-stem { font-size: 14px; color: #475569; }

/* 🛠️ 新增：填空题专用精美样式 */
.completion-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 500px;
}
.completion-item {
  display: flex;
  align-items: center;
}
.completion-prefix {
  font-size: 13px;
  color: #64748b;
  width: 80px;
  flex-shrink: 0;
}
.q-score-highlight {
  color: #67C23A;      /* ElementUI 经典成功绿 */
  font-weight: bold;   /* 字体加粗 */
  margin-right: 4px;   /* 与后面题干文字拉开微小间距 */
}
</style>
